import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, CreditCard, Upload, FileText, Download } from "lucide-react";
import { motion } from "framer-motion";

export default function C6Bank() {
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [tipoProcessamento, setTipoProcessamento] = useState('receita'); // 'receita' ou 'emprestimo'
  const fileInputRef = useRef(null);

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (ch === ',' && !inQuotes) {
        result.push(current);
        current = '';
        continue;
      }
      current += ch;
    }
    result.push(current);
    return result;
  };

  // Função para processar o CSV de entrada
  const processCSV = (csvText) => {
    const lines = csvText
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n');
    const transactions = [];
    
    let totalEntradas = 0;
    let totalSaidas = 0;
    let totalProcessadas = 0;
    let totalIgnoradas = 0;
    let valorTotalEntradas = 0;
    let valorTotalSaidas = 0;

    const headerIndex = lines.findIndex((l) => l.trim().startsWith('Data Lançamento,'));
    if (headerIndex === -1) {
      throw new Error('Cabeçalho do extrato não encontrado. Esperado: "Data Lançamento,Data Contábil,Título,Descrição,Entrada(R$),Saída(R$),Saldo do Dia(R$)"');
    }

    for (let i = headerIndex + 1; i < lines.length; i++) {
      const line = (lines[i] || '').trim();
      if (!line) continue;

      const values = parseCSVLine(line);
      if (values.length < 7) {
        totalIgnoradas++;
        continue;
      }

      const dataLancamento = (values[0] || '').trim();
      const titulo = (values[2] || '').trim();
      const descricao = (values[3] || '').trim();
      const entradaStr = (values[4] || '0').trim();
      const saidaStr = (values[5] || '0').trim();

      const entrada = parseFloat(entradaStr.replace(',', '.'));
      const saida = parseFloat(saidaStr.replace(',', '.'));

      const entradaValida = Number.isFinite(entrada) ? entrada : 0;
      const saidaValida = Number.isFinite(saida) ? saida : 0;

      if (entradaValida > 0) {
        totalEntradas++;
        const row = {
          data: dataLancamento,
          tipo: 'ENTRADA',
          descricao: titulo && descricao ? `${titulo} - ${descricao}` : (titulo || descricao),
          valor: entradaValida
        };
        transactions.push(row);
        totalProcessadas++;
        valorTotalEntradas += entradaValida;
      } else if (saidaValida > 0) {
        totalSaidas++;
        valorTotalSaidas += saidaValida;
        totalIgnoradas++;
      } else {
        totalIgnoradas++;
      }
    }

    return {
      data: transactions,
      summary: {
        totalEntradas,
        totalSaidas,
        totalProcessadas,
        totalIgnoradas,
        valorTotalEntradas,
        valorTotalSaidas,
        totalTransacoes: transactions.length
      }
    };
  };

  // Função para lidar com o upload do arquivo
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const result = processCSV(csvText);
        setCsvData(result.data);
        setSummary(result.summary);
        console.log('✅ CSV processado:', result.summary);
      } catch (error) {
        console.error('Erro ao processar CSV:', error);
        alert('Erro ao processar o arquivo CSV. Verifique o formato do arquivo.');
      } finally {
        setIsProcessing(false);
      }
    };
    
    reader.readAsText(file);
  };

  // Eventos de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Função para formatar data no formato DDMMAAAA
  const formatarDataCSV = (dataOriginal) => {
    const partes = (dataOriginal || '').trim().split('/');
    if (partes.length >= 3) {
      const [dia, mes, ano] = partes;
      return `${dia.padStart(2, '0')}${mes.padStart(2, '0')}${ano}`;
    }
    const anoAtual = new Date().getFullYear();
    const [dia, mes] = partes;
    return `${(dia || '').padStart(2, '0')}${(mes || '').padStart(2, '0')}${anoAtual}`;
  };

  // Função para remover vírgulas e converter para maiúsculo
  const formatarDescricao = (descricao) => {
    return descricao
      .replace(/,/g, '') // Remover vírgulas
      .toUpperCase(); // Converter para maiúsculo
  };

  // Função para determinar o código da conta crédito
  const determinarContaCredito = (descricao, tipo) => {
    const descricaoUpper = descricao.toUpperCase();
    const ehPrimepag = descricaoUpper.includes('PRIMEPAG');
    const ehEstornoPix = descricaoUpper.includes('PIX ESTORNADO') || descricaoUpper.includes('PIX ESTOMADO');
    const ehDavysonOmena = descricaoUpper.includes('DAVYSON OMENA');
    
    if (ehDavysonOmena) {
      return 25005;
    }

    if (tipo === 'receita') {
      // Opção 1: Processar Entradas de Transferências da Conta Receita e Demais Entrada Como Outras Receitas
      // Regra especial: estornos de PIX vão para a conta 4866
      if (ehEstornoPix) {
        return 4866;
      }
      if (ehPrimepag) {
        return 16; // PRIMEPAG -> Conta Receita
      }
      return 2893; // Demais -> Outras Receitas
    } else {
      // Opção 2: Processar Entradas PrimePag Como Empréstimos e Demais Entradas Como Clientes Diversos
      if (ehPrimepag) {
        return 1354; // PRIMEPAG -> Empréstimos
      }
      return 142; // Demais -> Clientes Diversos
    }
  };

  // Função para gerar CSV formatado
  const gerarCSVFormatado = () => {
    if (!csvData.length) {
      alert('Por favor, faça upload do arquivo CSV primeiro.');
      return;
    }

    const linhasCSV = [];

    console.log('📄 Tipo de processamento:', tipoProcessamento);

    csvData.forEach((row, index) => {
      const dataFormatada = formatarDataCSV(row.data);
      const contaDebito = 19; // Regra global: sempre 19 para entradas
      const contaCredito = determinarContaCredito(row.descricao, tipoProcessamento);
      const valor = row.valor.toFixed(2); // Float com 2 casas decimais
      const zero = 0;
      const descricaoFormatada = formatarDescricao(row.descricao);
      const descricao = `"${descricaoFormatada}"`;

      // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição
      const linha = `${dataFormatada},${contaDebito},${contaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      if (index < 5) {
        console.log(`📄 Linha ${index + 1} CSV: Débito ${contaDebito}, Crédito ${contaCredito}, Valor ${valor}`);
      }
    });

    console.log('📄 CSV gerado com', linhasCSV.length, 'linhas');

    // Criar e baixar o arquivo CSV
    const csvContent = linhasCSV.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Nome do arquivo baseado no mês dos lançamentos do CSV de entrada
    let nomeMes = 'MES';
    if (csvData.length) {
      const primeiraData = (csvData[0].data || '').trim(); // Ex.: "02/07"
      const partes = primeiraData.split('/');
      if (partes.length >= 2) {
        const mesNumero = partes[1].padStart(2, '0');
        const nomesMeses = {
          '01': 'JANEIRO',
          '02': 'FEVEREIRO',
          '03': 'MARCO',
          '04': 'ABRIL',
          '05': 'MAIO',
          '06': 'JUNHO',
          '07': 'JULHO',
          '08': 'AGOSTO',
          '09': 'SETEMBRO',
          '10': 'OUTUBRO',
          '11': 'NOVEMBRO',
          '12': 'DEZEMBRO',
        };
        nomeMes = nomesMeses[mesNumero] || 'MES';
      }
    }
    a.download = `C6_BANK_LANCAMENTOS_${nomeMes}.csv`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para calcular totais por tipo
  const calcularTotaisPorTipo = () => {
    if (!csvData.length) return { primepag: 0, demais: 0, countPrimepag: 0, countDemais: 0 };
    
    let totalPrimepag = 0;
    let totalDemais = 0;
    let countPrimepag = 0;
    let countDemais = 0;
    
    csvData.forEach(row => {
      const descricaoUpper = row.descricao.toUpperCase();
      if (descricaoUpper.includes('PRIMEPAG')) {
        totalPrimepag += row.valor;
        countPrimepag++;
      } else {
        totalDemais += row.valor;
        countDemais++;
      }
    });
    
    return { 
      primepag: totalPrimepag, 
      demais: totalDemais,
      countPrimepag,
      countDemais
    };
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Cabeçalho */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to={createPageUrl("Operations")}>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar para Operações
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <CreditCard className="w-8 h-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">C6 Bank</h1>
              <p className="text-gray-600 mt-1">Processamento de extratos bancários C6 Bank</p>
            </div>
          </div>
        </div>

        {/* Seleção do Tipo de Processamento */}
        <Card className="mb-8 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <CreditCard className="w-5 h-5" />
              Tipo de Processamento
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              Selecione como deseja processar as entradas do C6 Bank:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Opção 1: Receita */}
              <button
                onClick={() => setTipoProcessamento('receita')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  tipoProcessamento === 'receita'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    tipoProcessamento === 'receita' ? 'border-blue-500' : 'border-gray-300'
                  }`}>
                    {tipoProcessamento === 'receita' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Transferências da Conta Receita e Outras Receitas
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Processa entradas de transferências da conta receita e demais entradas como outras receitas.
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">PRIMEPAG:</span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded">Código 16</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Demais:</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">Código 2893</span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>

              {/* Opção 2: Empréstimo */}
              <button
                onClick={() => setTipoProcessamento('emprestimo')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  tipoProcessamento === 'emprestimo'
                    ? 'border-purple-500 bg-purple-50 shadow-md'
                    : 'border-gray-300 hover:border-purple-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    tipoProcessamento === 'emprestimo' ? 'border-purple-500' : 'border-gray-300'
                  }`}>
                    {tipoProcessamento === 'emprestimo' && (
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      Empréstimos e Clientes Diversos
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Processa entradas PrimePag como empréstimos e demais entradas como clientes diversos.
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">PRIMEPAG:</span>
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">Código 1354</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Demais:</span>
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">Código 142</span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            </div>
            
            {/* Indicador de seleção atual */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Selecionado:</strong> {tipoProcessamento === 'receita' ? 'Transferências da Conta Receita e Outras Receitas' : 'Empréstimos e Clientes Diversos'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Área de Upload */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload do Arquivo CSV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragOver 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-300 hover:border-purple-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-purple-100 rounded-full">
                  <FileText className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    {isProcessing ? 'Processando arquivo...' : 'Arraste e solte o arquivo CSV aqui'}
                  </p>
                  <p className="text-gray-500 mt-1">
                    ou clique para selecionar o arquivo
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  disabled={isProcessing}
                >
                  <Upload className="w-4 h-4" />
                  Selecionar Arquivo
                </Button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </CardContent>
        </Card>

        {/* Resumo e Ações */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Resumo do Processamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">Total de Entradas</p>
                    <p className="text-2xl font-bold text-blue-900">{summary.totalEntradas}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Processadas</p>
                    <p className="text-2xl font-bold text-green-900">{summary.totalProcessadas}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border-2 border-orange-200">
                    <p className="text-sm text-orange-600 font-medium">PrimePag Total</p>
                    <p className="text-xl font-bold text-orange-900">
                      {formatCurrency(calcularTotaisPorTipo().primepag)}
                    </p>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-lg border-2 border-indigo-200">
                    <p className="text-sm text-indigo-600 font-medium">Demais Entradas Total</p>
                    <p className="text-xl font-bold text-indigo-900">
                      {formatCurrency(calcularTotaisPorTipo().demais)}
                    </p>
                  </div>
                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <p className="text-sm text-emerald-600 font-medium">Valor Total</p>
                    <p className="text-xl font-bold text-emerald-900">{formatCurrency(summary.valorTotalEntradas)}</p>
                  </div>
                </div>

                <Button 
                  onClick={gerarCSVFormatado}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Baixar CSV Formatado
                </Button>
              </CardContent>
            </Card>

            {/* Preview dos Dados */}
            <Card>
              <CardHeader>
                <CardTitle>Preview dos Dados Processados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-purple-100">
                        <th className="border border-purple-300 px-4 py-2 text-left text-sm font-semibold">Data</th>
                        <th className="border border-purple-300 px-4 py-2 text-left text-sm font-semibold">Tipo</th>
                        <th className="border border-purple-300 px-4 py-2 text-left text-sm font-semibold">Descrição</th>
                        <th className="border border-purple-300 px-4 py-2 text-right text-sm font-semibold">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.slice(0, 10).map((row, index) => (
                        <tr key={index} className="hover:bg-purple-50">
                          <td className="border border-purple-200 px-4 py-2 text-sm">{row.data}</td>
                          <td className="border border-purple-200 px-4 py-2 text-sm">{row.tipo}</td>
                          <td className="border border-purple-200 px-4 py-2 text-sm">{row.descricao}</td>
                          <td className="border border-purple-200 px-4 py-2 text-sm text-right">
                            {formatCurrency(row.valor)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvData.length > 10 && (
                    <p className="text-sm text-gray-500 mt-4 text-center">
                      Mostrando 10 de {csvData.length} transações
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
